import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Drawer,
  Dropdown,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { GoOrganization } from "react-icons/go";
import { ItemType } from "antd/es/menu/interface";
import { ColumnsType } from "antd/es/table";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import TextNumeric from "components/TextNumeric";
import dayjs from "dayjs";
import { OrganisationDto } from "dtos/organisation.dto";
import DashboardLayout from "layout/DashboardLayout";
import { useEffect, useState } from "react";
import {
  TbArrowsUpDown,
  TbCheck,
  TbChevronLeft,
  TbChevronRight,
  TbClipboardCheck,
  TbColumns3,
  TbCopy,
  TbDots,
  TbEdit,
  TbExternalLink,
  TbKey,
  TbLoader2,
  TbPalette,
  TbProgress,
  TbSearch,
  TbSortAscendingNumbers,
  TbSortDescendingNumbers,
  TbTemplate,
  TbTimeline,
  TbX,
} from "react-icons/tb";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDateTime } from "utils/functions";
import StrategyModal from "./StrategyModal";
import ThemingModal from "components/Organisation/OrgConfigModal";
import StageManagerModal from "components/Stages/modal";

type T_Params = Parameters<typeof ApiService.organisation.useGetPaginated>[0];
type T_MetaKeys = keyof NonNullable<OrganisationDto["meta"]>;

function OrganisationsAll() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get("page") || "1") ?? 1;
  const limit = parseInt(searchParams.get("limit") || "20") ?? 20;
  const search = searchParams.get("search") ?? "";
  const slug = searchParams.get("slug") ?? "";
  const createDesk = ApiService.communicationChannel.useCreateDesk();
  const checkIdDeskExistsOrNot = ApiService.communicationChannel.useCheckDeskCorrelationExists();
  const [selectedOrganisation, setSelectedOrganisation] = useState<string | undefined>();
  const [params, setParams] = useState<T_Params>({ page, limit, filter: {}, search: search });
  const [isThemingModalOpen, setIsThemingModalOpen] = useState(false);
  const [themingSlug, setThemingSlug] = useState<string>("");
  const [stageSetupCode, setStageSetupCode] = useState<string | undefined>();
  const organisations = ApiService.organisation.useGetPaginated(params);
  const [isAddOrganisationModalOpen, setIsAddOrganisationModalOpen] = useState(false);
  const [isUpdateOrganisationModalOpen, setIsUpdateOrganisationModalOpen] = useState(false);
  const [isStrategyConfigModalOpen, setIsStrategyConfigModalOpen] = useState(false);
  const [showColumns, setShowColumns] = useState<
    Partial<Record<Exclude<keyof OrganisationDto, "meta"> | "actions" | T_MetaKeys, boolean>>
  >({
    name: true,
    code: true,
    createdAt: true,
    actions: true,
    website: true,
    company_name: true,
  });
  const sortOrder = params?.sort && Object.values(params?.sort)?.length > 0 ? Object.values(params?.sort)[0] : -1;

  const [addOrganisationForm] = Form.useForm();
  const [updateOrganisationForm] = Form.useForm();
  const [customStrategyConfigForm] = Form.useForm();
  const [dynamicStrategyConfigForm] = Form.useForm();

  const addOrganisation = ApiService.organisation.useAddOrganisation();
  const updateOrganisation = ApiService.organisation.useUpdateOrganisation();
  const toggleOrganisationStatus = ApiService.organisation.useToggleStatusOrganisation();
  const toggleOrganisationReady = ApiService.organisation.useToggleReadyOrganisation();
  const organisation = ApiService.organisation.useGetBySlug(slug);
  const navigate = useNavigate();

  const openEditModal = (record: OrganisationDto) => {
    setSelectedOrganisation(record.slug);
    setIsUpdateOrganisationModalOpen(true);
    updateOrganisationForm.setFieldsValue(record);
  };

  useEffect(() => {
    customStrategyConfigForm.resetFields();
    dynamicStrategyConfigForm.resetFields();

    const config = organisation.data?.strategy_config?.custom;
    const dynamicConfig = organisation.data?.strategy_config?.dynamic;

    if (config) customStrategyConfigForm.setFieldsValue(config);
    if (dynamicConfig) dynamicStrategyConfigForm.setFieldsValue({ strategies: dynamicConfig });
  }, [slug, organisation.data?.strategy_config]);

  const columns: ColumnsType<OrganisationDto> = [
    {
      key: "name",
      title: "Name",
      render: (_, record) => {
        return (
          <div className="inline-flex gap-2 items-center">
            <img src={record?.meta?.favicon} className="size-5 rounded-full" />
            <Typography.Text className="!font-medium">
              {record.name} <span className="ml-2">{record.disabled ? <Tag color="red">DISABLED</Tag> : ""}</span>
              <span className="ml-2">{record.isReady ? <Tag color="green">READY</Tag> : ""}</span>
            </Typography.Text>
          </div>
        );
      },
    },
    {
      key: "company_name",
      title: "Full Company Name",
      render: (_, record) => {
        return <Typography.Text type="secondary">{record?.meta?.company_name}</Typography.Text>;
      },
    },
    {
      key: "code",
      title: "Code",
      render: (_, record) => {
        return (
          <div className="inline-flex items-center p-2 bg-gray-50 border border-solid border-gray-200 rounded-full h-8">
            <Typography.Text
              className="text-xs font-semibold"
              type="secondary"
              copyable={{
                text: record?.code,
                icon: [
                  <TbCopy key="copy-icon" className="flex-shrink-0" />,
                  <TbClipboardCheck key="copied-icon" className="flex-shrink-0" />,
                ],
                tooltips: ["Copy", "Copied"],
              }}
            >
              {record?.code?.toUpperCase()}
            </Typography.Text>
          </div>
        );
      },
    },
    {
      key: "website",
      title: "Website",
      render: (_, record) => {
        return (
          <div className="flex flex-col gap-1">
            <Typography.Link className="inline-flex gap-1 items-center" href={record?.meta?.website} target="_blank">
              <span>{record?.meta?.website}</span>
              <TbExternalLink />
            </Typography.Link>
            {record?.available_domains?.map((el, i) => {
              return (
                <Typography.Link
                  key={i}
                  className="inline-flex gap-1 items-center"
                  href={el?.includes("http") ? el : `https://${el}`}
                  target="_blank"
                >
                  <span>{el}</span>
                  <TbExternalLink />
                </Typography.Link>
              );
            })}
          </div>
        );
      },
    },
    {
      key: "createdAt",
      dataIndex: "createdAt",
      title: "Created at",
      width: 100,
      render: (_, record) => {
        return (
          <TextNumeric>
            <Typography.Text type="secondary">
              <small>{record?.createdAt ? formatDateTime(record?.createdAt) : "-"}</small>
            </Typography.Text>
          </TextNumeric>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => {
        const menuItems: ItemType[] = [
          {
            key: "edit-organisation",
            icon: <TbEdit />,
            label: "Edit Details",
            onClick: () => {
              openEditModal(record);
            },
          },
          {
            key: "toggle-status-organisation",
            icon: record.disabled ? <TbCheck /> : <TbX />,
            label: record.disabled ? "Enable Organisation" : "Disable Organisation",
            onClick: () => {
              toggleOrganisationStatus.mutate(
                { slug: record.slug },
                {
                  onSuccess: () => {
                    message.success(
                      record.disabled ? "Organisation enabled succesfully." : "Organisation disabled succesfully.",
                    );
                    organisations.refetch();
                  },
                },
              );
            },
          },
          {
            key: "toggle-ready-state",
            icon: record.isReady ? <TbX /> : <TbCheck />,
            label: record.isReady ? "Unready Organisation" : "Ready Organisation",
            onClick() {
              toggleOrganisationReady.mutate(
                { slug: record.code, value: !record.isReady },
                {
                  onSuccess() {
                    message.success(`Organisation toggled to ${record.isReady ? "unready" : "ready"}`);
                    organisations.refetch();
                  },
                },
              );
            },
          },
          {
            key: "edit-strategy-config",
            icon: <TbEdit />,
            label: "Edit Strategy Configurations",
            onClick: () => {
              setSelectedOrganisation(record.slug);
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("slug", record.slug);
                return newParams;
              });
              setIsStrategyConfigModalOpen(true);
            },
          },
          {
            key: "view-org-emails",
            icon: <GoOrganization />,
            label: "Email Dashboard",
            onClick: () => {
              // setSearchParams((prev) => {
              //   const newParams = new URLSearchParams(prev);
              //   newParams.set("slug", record.slug);
              //   return newParams;
              // });
              // setIsStrategyConfigModalOpen(true);
              navigate(`/dashboard/emails/${record.slug}`);
            },
          },
          {
            key: "stages",
            icon: <TbProgress />,
            label: "Setup Stages",
            onClick: () => setStageSetupCode(record.code),
          },
          {
            key: "edit-theming",
            icon: <TbPalette />,
            label: "Organisation Config",
            onClick: () => {
              setThemingSlug(record.slug);
              setIsThemingModalOpen(true);
            },
          },
          {
            key: "create-pd-template",
            icon: <TbTemplate />,
            label: "Map PandaDoc Templates",
            onClick: () => {
              navigate(`/dashboard/application/key-mapping?org_code=${record.code}`);
            },
          },
          {
            key: "set-up-channel",
            icon: <TbKey />,
            label: "Setup Communication Channel",
            onClick: () => {
              checkIdDeskExistsOrNot.mutate(
                { correlationId: record.code },
                {
                  onSuccess: (res) => {
                    if (res.result.exists) {
                      navigate(
                        `/dashboard/organisation/${record.code}/channel?desk_id=${res.result.desk._id}&orgCode=${record.code}&src=org`,
                      );
                    } else {
                      createDesk.mutate(
                        {
                          correlation_id: record.code,
                          meta: {
                            name: record.name,
                            code: record.code,
                            slug: record.slug,
                            logo: record.meta?.logo,
                            favicon: record.meta?.favicon,
                          },
                        },
                        {
                          onSuccess: (res) => {
                            navigate(
                              `/dashboard/organisation/${record.code}/channel?desk_id=${res.result._id}&orgCode=${record.code}&src=org`,
                            );
                          },
                        },
                      );
                    }
                  },
                },
              );
              // if (record.desk_id) {
              //   navigate(`/dashboard/organisation/${record.code}/channel?desk_id=${record.desk_id}`);
              // } else {

              // }
            },
          },
        ];

        return (
          <>
            <Dropdown
              menu={{
                items: menuItems,
              }}
            >
              <Button icon={<TbDots />} />
            </Dropdown>
          </>
        );
      },
    },
  ];

  return (
    <DashboardLayout
      headerRight={
        <Button
          type="primary"
          onClick={() => {
            setIsAddOrganisationModalOpen(true);
          }}
        >
          Create Organisation
        </Button>
      }
    >
      <PageTitle pageTitle={[{ title: "Organisations" }]} title="Organisations" />
      <Modal
        centered
        title="Add New Organisation"
        open={isAddOrganisationModalOpen}
        onCancel={() => {
          setIsAddOrganisationModalOpen(false);
        }}
        onOk={() => {
          addOrganisationForm.submit();
        }}
        okText="Submit"
        okButtonProps={{ loading: addOrganisation.isPending }}
        styles={{
          body: {
            height: 600,
            overflowY: "auto",
          },
          content: {
            paddingInline: 0,
          },
          header: {
            paddingInline: 24,
          },
          footer: {
            paddingInline: 24,
          },
        }}
      >
        <Form
          layout="vertical"
          form={addOrganisationForm}
          onFinish={(values) => {
            addOrganisation.mutate(values, {
              onSuccess: (data) => {
                addOrganisationForm.resetFields();
                setIsAddOrganisationModalOpen(false);
                message.success("Organisation created succesfully.");
                organisations.refetch();
              },
            });
          }}
          className="!px-6"
        >
          <Form.Item name={"name"} label="Short Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name={"code"} label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name={"administrator_email"} label="Administrator Email">
            <Input />
          </Form.Item>
          <Form.Item name="available_domains" label="Domains">
            <Select mode="tags" placeholder="Enter domains" tokenSeparators={[",", " "]} />
          </Form.Item>

          <Form.Item label="Meta Information" className="!m-0">
            <Form.Item name={["meta", "website"]} label="Website" rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Space direction="vertical" style={{ width: "100%" }} className="mt-2 p-2 !gap-0">
              <Form.Item name={["meta", "title"]} label="Title">
                <Input placeholder="Enter title" />
              </Form.Item>

              <Form.Item name={["meta", "company_name"]} label="Company Name">
                <Input placeholder="Enter company name" />
              </Form.Item>

              <Form.Item name={["meta", "tagline"]} label="Tagline">
                <Input placeholder="Enter tagline" />
              </Form.Item>

              <Form.Item name={["meta", "logo"]} label="Logo">
                <Input placeholder="Enter logo link" />
              </Form.Item>

              <Form.Item name={["meta", "favicon"]} label="Favicon">
                <Input placeholder="Enter favicon link" />
              </Form.Item>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        centered
        title="Update Organisation"
        open={isUpdateOrganisationModalOpen}
        onCancel={() => {
          setIsUpdateOrganisationModalOpen(false);
        }}
        onOk={() => {
          updateOrganisationForm.setFieldsValue("");
          updateOrganisationForm.submit();
        }}
        okText="Submit"
        okButtonProps={{ loading: updateOrganisation.isPending }}
        styles={{
          body: {
            height: 600,
            overflowY: "auto",
          },
          content: {
            paddingInline: 0,
          },
          header: {
            paddingInline: 24,
          },
          footer: {
            paddingInline: 24,
          },
        }}
      >
        <Form
          layout="vertical"
          form={updateOrganisationForm}
          onFinish={(values) => {
            updateOrganisation.mutate(
              { slug: selectedOrganisation, body: values },
              {
                onSuccess: () => {
                  console.log(values);
                  message.success("Organisation updated succesfully.");
                  updateOrganisationForm.resetFields();
                  setIsUpdateOrganisationModalOpen(false);
                  organisations.refetch();
                },
              },
            );
          }}
          className="!px-6"
        >
          <Form.Item name={"name"} label="Name">
            <Input />
          </Form.Item>
          <Form.Item name={"code"} label="Code">
            <Input />
          </Form.Item>

          <Form.Item name={"administrator_email"} label="Administrator Email">
            <Input />
          </Form.Item>
          <Form.Item name="available_domains" label="Domains">
            <Select mode="tags" placeholder="Enter domains" tokenSeparators={[",", " "]} />
          </Form.Item>

          <Form.Item label="Meta Information" className="!m-0">
            <Space direction="vertical" style={{ width: "100%" }} className="mt-2 p-2">
              <Form.Item name={["meta", "website"]} label="Website" rules={[{ required: true }]}>
                <Input />
              </Form.Item>

              <Form.Item name={["meta", "title"]} label="Title">
                <Input placeholder="Enter title" />
              </Form.Item>

              <Form.Item name={["meta", "company_name"]} label="Company Name">
                <Input placeholder="Enter company name" />
              </Form.Item>

              <Form.Item name={["meta", "tagline"]} label="Tagline">
                <Input placeholder="Enter tagline" />
              </Form.Item>

              <Form.Item
                name={["meta", "logo"]}
                label="Logo"
                rules={[{ type: "url", message: "Logo link should be a valid link" }]}
              >
                <Input placeholder="Enter logo link" />
              </Form.Item>

              <Form.Item
                name={["meta", "favicon"]}
                label="Favicon"
                rules={[{ type: "url", message: "Favicon link should be a valid link" }]}
              >
                <Input placeholder="Enter favicon link" />
              </Form.Item>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <div className="flex justify-between gap-3 p-3">
        <div className="inline-flex gap-3 items-center">
          <Dropdown
            trigger={["click"]}
            menu={{
              items: columns.map((el) => {
                const key = String(el.key) as keyof typeof showColumns;
                return {
                  key: key,
                  label: (
                    <div className="flex gap-2" key={el.key}>
                      <Checkbox checked={showColumns[key]} />
                      <span>{el.title?.toString()}</span>
                    </div>
                  ),
                };
              }),
              onClick: ({ key }) => {
                setShowColumns((prev) => {
                  return { ...prev, [key]: !(prev as any)[key] };
                });
              },
            }}
          >
            <Button icon={<TbColumns3 />}>
              Columns{" "}
              <Tag bordered={false} className="!m-0 !bg-gray-100 !font-medium">
                {
                  columns.filter((el) => {
                    const key = String(el.key) as keyof typeof showColumns;
                    return showColumns[key];
                  }).length
                }
              </Tag>
            </Button>
          </Dropdown>
          {/* <Button
            icon={<LuListFilter />}
            onClick={() => {
              setIsFilterOpen(true);
            }}
          >
            Filter{" "}
            {params.filter ? (
              <Tag bordered={false} className="!m-0 !bg-gray-100 !font-medium">
                {
                  Object.values(params.filter)
                    .map((el) => el)
                    .flat()?.length
                }
              </Tag>
            ) : null}
          </Button> */}
          <Space.Compact>
            <Dropdown
              trigger={["click"]}
              menu={{
                selectable: true,
                selectedKeys: params?.sort ? Object.keys(params.sort) : [],
                onSelect: ({ key }) => setParams((prev) => ({ ...prev, sort: { [key]: sortOrder } as any })),
                items: [
                  {
                    key: "createdAt",
                    label: "Created at",
                  },
                ],
              }}
            >
              <Button
                className="border-gray-300"
                variant={params?.sort ? "filled" : "outlined"}
                color={params?.sort ? "primary" : "default"}
                icon={<TbArrowsUpDown />}
              >
                Sort
              </Button>
            </Dropdown>
            {params?.sort && Object.keys(params?.sort)?.length > 0 ? (
              <>
                <Button
                  color="default"
                  className="border-gray-300 px-2"
                  icon={sortOrder === -1 ? <TbSortDescendingNumbers /> : <TbSortAscendingNumbers />}
                  onClick={() =>
                    setParams((prev) => {
                      if (!prev?.sort) return prev;
                      const key = Object.keys(prev?.sort)?.[0];
                      return { ...prev, sort: { [key]: sortOrder === 1 ? -1 : 1 } };
                    })
                  }
                >
                  <Typography.Text className="text-xs w-10">{sortOrder === -1 ? "DESC" : "ASC"}</Typography.Text>
                </Button>
                <Button icon={<TbX />} onClick={() => setParams((prev) => ({ ...prev, sort: undefined }))} />
              </>
            ) : null}
          </Space.Compact>
        </div>
        <div className="inline-flex gap-3 items-center">
          <Input
            prefix={<TbSearch />}
            defaultValue={search}
            placeholder="Search organisations..."
            onPressEnter={(e) => {
              setParams((prev) => ({ ...prev, search: e.currentTarget.value, page: 1 }));
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("search", e.currentTarget.value);
                newParams.set("page", "1");
                return newParams;
              });
            }}
            allowClear
            onClear={() => {
              setParams((prev) => ({ ...prev, search: undefined, page: 1 }));
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("page", "1");
                newParams.delete("search");
                return newParams;
              });
            }}
          />
        </div>
      </div>
      <StrategyModal
        open={isStrategyConfigModalOpen}
        onClose={() => setIsStrategyConfigModalOpen(false)}
        customStrategyConfigForm={customStrategyConfigForm}
        dynamicStrategyConfigForm={dynamicStrategyConfigForm}
        refetchOrg={() => {
          organisations.refetch();
        }}
        selectedOrg={selectedOrganisation as string}
      />
      <Table<OrganisationDto>
        scroll={{ x: "max-content", y: "calc(100vh - 202px)" }}
        virtual={false}
        size="small"
        columns={columns?.filter((el) => {
          if (el?.key?.toString() && el?.key?.toString() in showColumns && (showColumns as any)[el?.key?.toString()]) {
            return el;
          }
        })}
        loading={{
          spinning: organisations.isFetching,
          indicator: <TbLoader2 size={48} className="text-brand-500 animate-spin" />,
        }}
        dataSource={organisations.data?.result.data}
        rowKey={"_id"}
        pagination={{
          hideOnSinglePage: false,
          showQuickJumper: true,
          showPrevNextJumpers: true,
          nextIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronRight size={20} />}
            />
          ),
          prevIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronLeft size={20} />}
            />
          ),
          showTitle: true,
          showTotal: (total, range) => {
            return `Showing ${range[0]}-${range[1]} of ${total} organisations`;
          },
          showSizeChanger: true,
          total: organisations.data?.result?.totalObjects,
          current: params.page,
          pageSize: params.limit,
          onChange: (page, pageSize) => {
            setParams({ page: page, limit: pageSize, filter: {}, search: search });
            setSearchParams((prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set("page", page.toString());
              newParams.set("limit", pageSize.toString());
              if (search) {
                newParams.set("search", search.toString());
              }
              return newParams;
            });
          },
        }}
      />
      {/* <FilterDrawer
        open={isFilterOpen}
        onClose={(filter) => {
          if (filter) {
            setParams({ ...params, filter: filter });
          }
          setIsFilterOpen(false);
        }}
        existingParams={params}
      /> */}
      <ThemingModal
        open={isThemingModalOpen}
        onClose={() => setIsThemingModalOpen(false)}
        slug={themingSlug}
        onSuccess={() => organisations.refetch()}
      />
      <StageManagerModal orgCode={stageSetupCode} onClose={() => setStageSetupCode(undefined)} />
    </DashboardLayout>
  );
}

type T_Filter = T_Params["filter"];
function FilterDrawer({
  open,
  onClose,
  existingParams,
}: {
  open: boolean;
  onClose: (filter?: T_Filter) => void;
  existingParams: T_Params;
}) {
  const [filter, setFilter] = useState<T_Filter>(existingParams.filter);
  const paginatedAllUsers = ApiService.user.useGetPaginated({ page: 1, limit: 100, role: [] });

  return (
    <Drawer
      open={open}
      onClose={() => onClose()}
      footer={
        <div className="flex gap-2">
          <Button
            className="flex-1"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            variant="solid"
            color="primary"
            onClick={() => {
              onClose(filter);
            }}
          >
            Filter
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <Typography.Text type="secondary">Last Step</Typography.Text>
        <Divider className="!my-2" />
        <Typography.Text type="secondary">Created at</Typography.Text>
        <div className="flex flex-col gap-2 ml-4" key={"created_at"}>
          <Typography.Text>Start Date</Typography.Text>
          <DatePicker
            value={filter?.createdAt?.startDate ? dayjs(filter?.createdAt?.startDate) : undefined}
            onChange={(date) => {
              setFilter((prev) => ({ ...prev, createdAt: { startDate: date ? date.toISOString() : undefined } }));
            }}
            disabledDate={(date) => {
              if (filter?.createdAt?.endDate) {
                return dayjs(filter?.createdAt?.endDate).isBefore(date);
              } else return false;
            }}
          />
          <Typography.Text>End Date</Typography.Text>
          <DatePicker
            value={filter?.createdAt?.endDate ? dayjs(filter?.createdAt?.endDate) : undefined}
            onChange={(date) => {
              setFilter((prev) => ({ ...prev, createdAt: { endDate: date ? date.toISOString() : undefined } }));
            }}
            disabledDate={(date) => {
              if (filter?.createdAt?.startDate) {
                return dayjs(filter?.createdAt?.startDate).isAfter(date);
              } else return false;
            }}
          />
        </div>
        <Divider className="!my-2" />
        <Typography.Text>Affiliates</Typography.Text>
        <Select
          allowClear
          showSearch
          prefix={<TbSearch />}
          placeholder="Search users..."
          mode="multiple"
          loading={paginatedAllUsers.isPending}
          options={paginatedAllUsers.data?.result?.data?.map((user) => ({ value: user._id, label: user?.name }))}
          filterOption={(searchTerm, option) => {
            return !!option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase());
          }}
        />
      </div>
    </Drawer>
  );
}

export default OrganisationsAll;
